import { useEffect, useState } from 'react';

export type Async<T> =
  | { state: 'loading' }
  | { state: 'ready'; data: T }
  | { state: 'error'; error: Error };

/**
 * Runs a loader once per dependency change; ignores results from stale runs.
 * With `keep`, a reload keeps showing the last data until the new data arrives
 * (no flash of empty rows after every action).
 */
export function useAsync<T>(load: () => Promise<T>, deps: unknown[], opts: { keep?: boolean } = {}): Async<T> {
  const [value, setValue] = useState<Async<T>>({ state: 'loading' });
  useEffect(() => {
    let live = true;
    setValue((v) => (opts.keep && v.state === 'ready' ? v : { state: 'loading' }));
    load().then(
      (data) => live && setValue({ state: 'ready', data }),
      (error: unknown) => live && setValue({ state: 'error', error: error instanceof Error ? error : new Error(String(error)) }),
    );
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return value;
}
